import { Injectable, OnDestroy } from '@angular/core';
import { CacheService } from '@core/cache.service';
import {
  StatefulService
} from '@core/services/stateful-service-management/stateful-service-interface/stateful-service-interface';
import { Classroom } from '@models/classroom';
import { IProductLine } from '@models/product-line';
import { IProductLineVariant } from '@models/product-variant';
import { VariantType } from '@shared/enums/variant-type';
import { Helpers } from '@shared/helpers';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { LearningFacade } from 'src/app/classrooms/store/learning.facade';


/**
 * Student caches selected classroom, produdctLineKey and VariantType
 */
@Injectable({ providedIn: 'root' })
export class StudentStateService implements OnDestroy, StatefulService {

  private _classrooms$ = new BehaviorSubject<Classroom[]>(null);
  private _selectedClassroom$ = new BehaviorSubject<Classroom>(null);
  /**
   * Product Line Array of all Student Products
   */
  private _programs$ = new BehaviorSubject<IProductLine[]>(null);
  /**
   * Product Line Array of Student Products for Selected Classroom
   */
  private _programsForClassroom$ = new BehaviorSubject<IProductLine[]>(null);
  /**
   * Product Line Id
   */
  private _selectedProgramKey$ = new BehaviorSubject<string>(this.cacheService.productLineId);
  /**
   * Variant Type
   */
  private _selectedVariantType$ = new BehaviorSubject<VariantType>(this.cacheService.variant as VariantType);

  private readonly subscriptions = new Subscription();

  constructor(
    private learningFacade: LearningFacade,
    private cacheService: CacheService,
  ) {
    // Product lines and classrooms are loaded into state by the relevant guards.
    this.subscriptions.add(this.learningFacade.classrooms$.subscribe(this._classrooms$));
    this.subscriptions.add(this.learningFacade.selectedClassroom$.subscribe(this._selectedClassroom$));
    this.subscriptions.add(this.learningFacade.productLines$.subscribe((this._programs$)));

    // If nothing currently selected, will use cached value if available
    // If no cache, automatically selects the classroom when there is only 1
    this.subscriptions.add(
      this.classrooms$.pipe(
        distinctUntilChanged(),
        filter(classrooms => !Helpers.isNullOrUndefined(classrooms)),
      ).subscribe((classrooms) => {
        if (!this.selectedClassroom && this.cacheService.classroomId) {
          const cachedClassroom = classrooms.find(classroom => classroom.classroomId === this.cacheService.classroomId);
          if (cachedClassroom) {
            this.selectClassroom(cachedClassroom);
          } else if (classrooms.length === 1) {
            this.cacheService.classroomId = classrooms[0].classroomId;
            this.selectClassroom(classrooms[0]);
          }
        }
      })
    );
  }

  /** Resets the stateful properties of this service. */
  reset(): void {
    this._classrooms$.next(null);
    this._selectedClassroom$.next(null);
    this._programs$.next(null);
    this._programsForClassroom$.next(null);
    this._selectedProgramKey$.next(null);
    this._selectedVariantType$.next(null);

    this.cacheService.productLineId = null;
    this.cacheService.variant = null;
    this.cacheService.classroomId = null;

    this.learningFacade.selectClassroom(null);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  get classrooms(): Classroom[] {
    return (this._classrooms$.value ?? []).filter(c => c.licenses?.length > 0);
  }

  get classrooms$(): Observable<Classroom[]> {
    return this._classrooms$.asObservable().pipe(
      map(classrooms => (classrooms ?? []).filter(c => c.licenses?.length > 0))
    );
  }

  get selectedClassroom(): Classroom | undefined {
    return this._selectedClassroom$.value;
  }

  get selectedClassroom$(): Observable<Classroom> {
    return this._selectedClassroom$.asObservable();
  }

  get programs(): IProductLine[] {
    return this._programs$.value;
  }

  /**
   * Gets all available ProductLines for a student.
   */
  get programs$(): Observable<IProductLine[]> {
    return this._programs$.asObservable();
  }

  /**
   * Gets the product line key that was selected.
   *
   * If none was selected, get it from cache
   */
  get selectedProgram(): IProductLine {
    if (!this._selectedProgramKey$?.value) {
      const cachedProductLineKey = this.cacheService.productLineId;
      return this.programs.find(program => program.productLineKey === cachedProductLineKey);
    }
    return this.programs.find(program => program.productLineKey === this._selectedProgramKey$.value);
  }

  /**
   * Gets the product line key that was selected.
   */
  get selectedProgramKey(): string {
    return this._selectedProgramKey$.value;
  }

  /**
   * Observable for the product line key that was selected.
   */
  get selectedProgramKey$(): Observable<string> {
    return this._selectedProgramKey$.asObservable();
  }

  /**
   * Gets the variant type that was selected.
   */
  get selectedVariantType$(): Observable<VariantType> {
    return this._selectedVariantType$.asObservable();
  }

  /**
   * Gets the product line from the student's product lines based on their stored selection.
   *
   * The product line is useful for retrieving the full name of the program and logo.
   *
   * If the student has not chosen either or no longer has access to it in their navigation, then this resolves to
   * null.
   */
  get selectedProgram$(): Observable<IProductLine> {
    return this.selectedProgramKey$
      .pipe(
        switchMap(productLineKey => (productLineKey
          ? this.learningFacade.getProductLineByKey(productLineKey)
          : of(null)
        )),
      );
  }

  /**
   * Gets the product line variant from the student's stored selected product line and variant type.
   *
   * The variant is useful for retrieving the variant name, variant type as well as SKUs (SchoolLicense) and the
   * product components contained.
   *
   * If the student has not chosen either or no longer has access to it in their navigation, then this resolves to
   * null.
   */
  get selectedVariant$(): Observable<IProductLineVariant> {
    return this._selectedVariantType$.asObservable()
      .pipe(
        withLatestFrom(this._selectedProgramKey$),
        switchMap(([variantType, productLineKey]) => (productLineKey && variantType
          ? this.learningFacade.getProductLineVariant(productLineKey, variantType)
          : of(null)
        )),
      );
  }

  /**
   * Gets the current value of available ProductLines for a specific classroom
   * from the _programsForClassroom$ Observable
   */
  get productsForSelectedClassroom(): IProductLine[] {
    return this._programsForClassroom$.value;
  }

  /**
   * Gets all available ProductLines for a specific classroom as a IProduct[] Observable
   */
  get productsForSelectedClassroom$(): Observable<IProductLine[]> {
    return this._programsForClassroom$.asObservable();
  }

  /**
   * Sets the _selectedClassroom$ in this service, and updates the Learning Facade and cache with selected classroom.
   *
   * Updates the _programsForClassroom$ in this service with the products available for the selected classroom.
   */
  selectClassroom(classroom: Classroom) {
    // Set cache so student can navigate back from DR
    this.cacheService.classroomId = classroom?.classroomId;
    this._selectedClassroom$.next(classroom);
    this.learningFacade.selectClassroom(classroom);
    // Set available programs for the selected class
    const programsForClassroom = this.getProductLinesForClassroom(this.selectedClassroom, this.programs);
    this._programsForClassroom$.next(programsForClassroom);
  }

  /**
   * Stores the student's selected ProductLine and VariantType in this service's state.
   *
   * Updates the cache to be used to restore sessions when coming back from DR
   */
  selectProgramAndVariantType(productLineKey: string, variantType: VariantType): void {
    this._selectedProgramKey$.next(productLineKey);
    this.cacheService.productLineId = productLineKey;
    this._selectedVariantType$.next(variantType);
    this.cacheService.variant = variantType;
  }

  /**
   * Reduces all product lines and variants into product lines, variants used in a classroom.
   *
   * This is to isolate the products for a classroom.
   */
  private getProductLinesForClassroom(classroom: Classroom, productLines: IProductLine[]): IProductLine[] {
    if (classroom) {
      return productLines?.reduce((ret, pl) => {
        const productType = Helpers.getProductTypeFromProductLineId(pl.productLineKey);
        const hasProductType = classroom
          .licenses
          .findIndex(l => l.productType === productType) !== -1;
        if (hasProductType) {
          const variants = pl.variants.filter(v => (classroom.licenses.findIndex(l => (
            l.productType === productType && l.variantType === v.variantType)) !== -1
          ));
          if (variants.length > 0) {
            return ret.concat({
              ...pl,
              variants,
            });
          }
        }
        return ret;
      }, []);
    }

    return [];
  }
}
